import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { DollarSign, Zap, Gauge, Cpu } from 'lucide-react';
import pipelineService from '../../services/pipelineService';
import { StatCard, PageHeader, Spinner, InfoHint } from '../../components/ui';
import { HELP, prettyStream } from '../../lib/metrics';
import '../../styles/theme.css';

const AXIS = { fontSize: 11, fill: 'var(--mx-text-subtle)' };
const tip = { background: 'var(--mx-surface)', border: '1px solid var(--mx-border)',
  borderRadius: 8, fontSize: 12, boxShadow: 'var(--mx-shadow-md)' };
const pct = (v: number) => `${Math.round((v || 0) * 100)}%`;

const Panel: React.FC<{ title: string; help?: string; note?: string; children: React.ReactNode }> =
  ({ title, help, note, children }) => (
    <div className="mx-card">
      <div className="mx-card-head"><h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {title}{help && <InfoHint text={help} />}</h3></div>
      <div className="mx-card-pad">
        {children}
        {note && <p className="mx-subtle" style={{ fontSize: 11, marginTop: 8 }}>{note}</p>}
      </div>
    </div>
  );

const PipelineDashboard: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [streams, setStreams] = useState<any[]>([]);
  const [trend, setTrend] = useState<any[]>([]);
  const [cost, setCost] = useState<any[]>([]);
  const [energy, setEnergy] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, sc, at, c, en] = await Promise.all([
          pipelineService.summary(), pipelineService.streamComparison(),
          pipelineService.accuracyTrend(), pipelineService.cost(), pipelineService.energy(),
        ]);
        setSummary(s); setStreams(sc.streams || []);
        setTrend((at.points || []).map((p: any, i: number) => ({ i, ...p })));
        setCost(c.models || []); setEnergy(en.streams || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div><PageHeader title="Telemetry" /><div className="mx-card mx-card-pad"><Spinner label="Loading telemetry…" /></div></div>;
  if (!summary) return <div><PageHeader title="Telemetry" /><div className="mx-card mx-card-pad mx-muted"
    style={{ textAlign: 'center', padding: '48px 24px' }}>Couldn’t load telemetry — the API may be restarting. Refresh to retry.</div></div>;
  const e = summary.energy_joules || {};

  return (
    <div>
      <PageHeader title="Telemetry" subtitle="Cost, energy, and accuracy across the three extraction methods." />

      <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 16 }}>
        <StatCard label="Total cost" value={`$${(summary.total_cost_usd || 0).toFixed(2)}`}
          icon={<DollarSign size={16} />} help={HELP.cost} />
        <StatCard label="Energy measured" value={`${Math.round(e.measured || 0)} J`} icon={<Zap size={16} />}
          hint="local models" help={HELP.energyMeasured} />
        <StatCard label="Energy estimated" value={`${Math.round(e.estimated || 0)} J`} icon={<Zap size={16} />}
          hint="hosted APIs" help={HELP.energyEstimated} />
        <StatCard label="Accuracy" value={pct(summary.avg_f1 || 0)} icon={<Gauge size={16} />} help={HELP.accuracy} />
      </div>

      <div className="mx-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Panel title="Method comparison" help={HELP.methodComparison}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={streams} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
              <XAxis dataKey="stream" tickFormatter={prettyStream} tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 1]} tickFormatter={pct} tick={AXIS} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={tip} cursor={{ fill: 'var(--mx-surface-2)' }}
                formatter={(v: any) => pct(v as number)} labelFormatter={prettyStream} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="precision" fill="var(--mx-c1)" radius={[3, 3, 0, 0]} name="Correctness" />
              <Bar dataKey="recall" fill="var(--mx-c2)" radius={[3, 3, 0, 0]} name="Coverage" />
              <Bar dataKey="f1" fill="var(--mx-c5)" radius={[3, 3, 0, 0]} name="Accuracy" />
              <Bar dataKey="completeness" fill="var(--mx-c3)" radius={[3, 3, 0, 0]} name="Completeness" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Accuracy trend" help={HELP.accuracyTrend}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
              <XAxis dataKey="i" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 1]} tickFormatter={pct} tick={AXIS} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={tip} formatter={(v: any) => pct(v as number)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="f1" stroke="var(--mx-c5)" strokeWidth={2} dot={false} name="Accuracy" />
              <Line type="monotone" dataKey="completeness" stroke="var(--mx-c3)" strokeWidth={2} dot={false} name="Completeness" />
              <Line type="monotone" dataKey="hallucination_rate" stroke="var(--mx-c4)" strokeWidth={2} dot={false} name="Made-up values" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Cost by model" help={HELP.cost}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cost}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
              <XAxis dataKey="model" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tip} cursor={{ fill: 'var(--mx-surface-2)' }} />
              <Bar dataKey="cost_usd" fill="var(--mx-c2)" radius={[3, 3, 0, 0]} name="cost (USD)" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Energy by method" help={HELP.energyMeasured}
          note="Measured (local models) and estimated (hosted APIs) are shown separately and never summed.">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={energy}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
              <XAxis dataKey="stream" tickFormatter={prettyStream} tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tip} cursor={{ fill: 'var(--mx-surface-2)' }} labelFormatter={prettyStream} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="measured" fill="var(--mx-c2)" radius={[3, 3, 0, 0]} name="measured" />
              <Bar dataKey="estimated" fill="var(--mx-border-strong)" radius={[3, 3, 0, 0]} name="estimated" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
};

export default PipelineDashboard;
